/**
 * Layout and content constants of the AI support chat (plan §3.2–§3.5).
 * Sizes are px. Insets are logical (inline-end = left in Arabic, right in
 * English); never translate them into `left` / `right`.
 */

/**
 * Desktop floating panel (shell A, plan §12): it sits above the launcher,
 * `PANEL_GAP` over it, at the launcher's inline-end inset.
 */
export const PANEL_WIDTH = 380;

/** Tallest the floating panel gets (it shrinks on short screens). */
export const PANEL_MAX_HEIGHT = 640;

/** Shortest it gets before it may reach over the app bar. */
export const PANEL_MIN_HEIGHT = 360;

/** Space between the launcher and the floating panel. */
export const PANEL_GAP = 16;

/** Room kept above the floating panel: the 64 px app bar + a gap. */
export const PANEL_TOP_CLEARANCE = 80;

/** Mobile bottom sheet: the dimmed page stays visible above it. */
export const SHEET_TOP_GAP = 72;

/**
 * Launcher distance from the inline-end edge (§3.2). The merchant layouts
 * render the vertical Help Center tab on that edge, covering about 5–41 px
 * (src/components/common/HelpCenter.js), so the launcher clears it there.
 * The admin layout has no Help tab.
 */
export const LAUNCHER_INSET_END = Object.freeze({
  withHelpTab: 64,
  default: 24,
});

/** Launcher distance from the bottom edge, as a responsive `sx` value. */
export const LAUNCHER_INSET_BOTTOM = Object.freeze({ xs: 16, sm: 24 });

/** Launcher diameter, as a responsive `sx` value (MUI Fab large / medium). */
export const LAUNCHER_SIZE = Object.freeze({ xs: 48, sm: 56 });

/**
 * z-index helpers, relative to the MUI theme scale:
 * - `launcher`: speedDial (1050), below the app bar, drawers, modals,
 *   snackbars and joyride tours (10000);
 * - `panel`: drawer + 2 (1202), above the app bar and the Help tab (1200)
 *   when a short screen makes the floating panel reach them, below modals
 *   (1300).
 */
export const Z_INDEX = Object.freeze({
  launcher: (theme) => theme.zIndex.speedDial,
  panel: (theme) => theme.zIndex.drawer + 2,
});

/**
 * MUI's Slide / Grow / Zoom ignore the OS "reduce motion" setting: the
 * launcher and the panel pass `timeout` 0 when this matches.
 */
export const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/** `sx` of text read by screen readers but not shown (live regions). */
export const VISUALLY_HIDDEN = Object.freeze({
  position: "absolute",
  width: "1px",
  height: "1px",
  margin: "-1px",
  padding: 0,
  border: 0,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
});

/**
 * `sx` (theme callback) for a container: a 2 px primary outline around every
 * MUI button-like control inside it (Button, IconButton, Chip, ListItemButton)
 * focused from the keyboard. MUI's own cue, a faint ripple or a 12 % grey
 * background, is easy to miss (plan §3.9 "visible keyboard focus").
 */
export const focusRingSx = (theme) => ({
  "& .MuiButtonBase-root.Mui-focusVisible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: "2px",
  },
});

const PLACEHOLDER = Object.freeze({ color: "text.secondary", opacity: 1 });

/**
 * `sx` of a TextField whose placeholder is its only visible label: MUI's
 * placeholder (42 % opacity, about 3:1) becomes `text.secondary` (5.7:1).
 *
 * Vendor selectors, as MUI's InputBase uses: a plain `::placeholder` crashes
 * the whole app in Arabic (the RTL Emotion cache runs stylis 4.3's
 * `prefixer` on Emotion's stylis 4.2 output, src/StyleWrapper.js).
 */
export const PLACEHOLDER_SX = Object.freeze({
  "& .MuiInputBase-input::-webkit-input-placeholder": PLACEHOLDER,
  "& .MuiInputBase-input::-moz-placeholder": PLACEHOLDER,
});

/**
 * The composer shows how many characters are left once the trimmed text is
 * within this many of `maxMessageChars` (§3.6).
 */
export const CHARS_LEFT_THRESHOLD = 200;

/**
 * Days conversations are kept when `/eligibility` doesn't say
 * (`retentionDays`; backend `AI_CHAT_RETENTION_DAYS`, §7.1).
 */
export const RETENTION_FALLBACK_DAYS = 90;

/** i18n keys (namespace `aiSupport`) of the home suggestion chips (§3.5). */
export const SUGGESTION_KEYS = Object.freeze([
  "suggestions.s1",
  "suggestions.s2",
  "suggestions.s3",
  "suggestions.s4",
]);
